//NPM Modules
import { writable } from 'svelte/store';
import collect from 'collect.js';
import { get } from 'svelte/store';
import dayjs from 'dayjs';
// Models
import Alert from '@/Models/Alert.js';
import Race from '@/Models/Race.js';
import Meeting from '@/Models/Meeting';
// Stores
import ACLStore from '@/Stores/ACLStore.js';
import { lastAlertShown, lastAlertsPoll } from '@/Stores/Store.js';
import {
	mergeCollectionStore,
	mergeModelDataInCollectionStore,
	mergeOrAddModelDataInCollectionStore
} from '@/Stores/StoreHelpers.js';
import PreferenceStore from '@/Stores/PreferenceStore.js';
// Constants
import { AlertLevel } from '@/Constants/Alert.js';
import { BetCategory } from '@/Constants/Bet.js';
// Services
import { notify } from '@/Services/Notify.js';

/**
 * Our Store can be used 2 ways in svelte components
 * 1) ($ prefix) $StoreName to reference writable store collection with all
 * collect.js methods available
 * 2) (Direct) StoreName.method() to call a method on the store
 */
const init = () => {
	let _store;
	let preferenceFilter = [BetCategory.Racing, BetCategory.Sport];
	let alertsMuted = false;
	let alertNotificationSound = 'glock';
	let lastAlertShownId = 0;
	let firstLoad = true;

	let highestAlertLevel = false;
	let alertCount = 0;
	let topAlertId = 0;

	const { subscribe, set, update } = writable(collect());

	const filterAlertsToPreferences = (alert) => {
		if (alert.dismissed) {
			return false;
		}
		if (preferenceFilter.includes(BetCategory.Racing) && alert.category === BetCategory.Racing) {
			return true;
		} else if (preferenceFilter.includes(BetCategory.Sport) && alert.category === BetCategory.Sport) {
			return true;
		}

		return false;
	};

	const onAlertStoreUpdate = (alertCollection) => {
		highestAlertLevel = false;
		const filteredAlerts = alertCollection.filter(filterAlertsToPreferences(alert));
		alertCount = filteredAlerts.count();
		filteredAlerts.each((alert) => {
			if (highestAlertLevel === false) {
				highestAlertLevel = alert.level;
			}
			if (alert.level < highestAlertLevel) {
				highestAlertLevel = alert.level;
			}
			if (highestAlertLevel === AlertLevel.Danger) {
				return false;
			}
		});
		const topAlert = alertCollection.sortByDesc('created_at').first();
		if (topAlert) {
			topAlertId = topAlert.id;
		}
	};

	subscribe((alertCollection) => {
		_store = alertCollection;
		if (alertCollection) {
			onAlertStoreUpdate(alertCollection);
		}
	});

	PreferenceStore.subscribe((value) => {
		if (value) {
			alertsMuted = value.app.alertsMuted;
			alertNotificationSound = value.app.alertNotificationSound;
			if (preferenceFilter !== value.app.incomingFilter) {
				preferenceFilter = value.app.incomingFilter;
				if (_store) {
					onAlertStoreUpdate(_store);
				}
			}
		}
	});

	lastAlertShown.subscribe((lastAlertShown) => {
		if (lastAlertShown) {
			lastAlertShownId = lastAlertShown;
		}
	});

	const methods = {
		getAlerts(sinceLastPoll = true) {
			const alert = new Alert();
			if (sinceLastPoll) {
				const lastPoll = get(lastAlertsPoll);
				if (lastPoll !== false) {
					alert.where('min_created_at', lastPoll.format('YYYY-MM-DD HH:mm:ss'));
				} else {
					sinceLastPoll = false;
				}
			}
			return alert
				.with(['source'])
				.get()
				.then((result) => {
					lastAlertsPoll.set(dayjs());
					let alerts = collect(result.data).filter((alert) => {
						if (alert.dismissed) {
							return true;
						}
						if (ACLStore.granted(alert.permission)) {
							return true;
						}
					});
					if (alerts.count() > 0) {
						if (sinceLastPoll) {
							mergeCollectionStore(this, alerts);
						} else {
							set(alerts);
						}
						this.recomputeAlertLevels();
						if (lastAlertShownId !== 0 && !firstLoad) {
							for (const alert of alerts) {
								this.showAlertNotificationAndLogId(alert);
							}
						}
					}
					firstLoad = false;
				})
				.catch((e) => {
					console.log(e);
				});
		},

		addAlertFromData(data) {
			const alert = new Alert();
			let result = alert.respond(data);
			if (result && result instanceof Alert) {
				mergeOrAddModelDataInCollectionStore(result, this);
				this.showAlertNotificationAndLogId(result);
				this.playAlertNotification();
			}
		},

		recomputeAlertLevels() {
			update((items) => {
				items = items.transform((alert) => {
					if (alert && typeof alert.getCurrentAlertLevel === 'function') {
						const newLevel = alert.getCurrentAlertLevel();
						if (alert['level'] !== newLevel) {
							alert['level'] = newLevel;
						}
					}
					return alert;
				});
				return items;
			});
		},

		removeAlert(alertId) {
			update((items) => items.reject((item) => item.id === alertId));
			this.recomputeAlertLevels();
		},

		updateAlert(data) {
			if (data) {
				mergeModelDataInCollectionStore(data, this);
				this.recomputeAlertLevels();
			}
		},

		getAlertCount() {
			return alertCount;
		},

		getHighestAlertLevel() {
			return highestAlertLevel;
		},

		getTopAlertId() {
			return topAlertId;
		},

		async getFilteredAlertsForUser(showAllAlerts, page, numberPerPage = 50) {
			const filteredAlerts = _store.filter(filterAlertsToPreferences(alert));
			if (showAllAlerts) {
				return filteredAlerts.sortByDesc('created_at').all();
			}
			return filteredAlerts.sortByDesc('created_at').forPage(page, numberPerPage).all();
		},

		getAlertForRace(raceId, alertType) {
			return _store
				.filter((alert) => {
					if (alert.source instanceof Race) {
						if (raceId === alert.source.id) {
							if (typeof alertType === 'undefined') {
								return true;
							} else if (alertType === alert.alert_type.id) {
								return true;
							}
						}
					}
				})
				.first();
		},

		getAlertForMeeting(meetingId, alertType) {
			return _store
				.filter((alert) => {
					if (alert.source instanceof Meeting) {
						if (meetingId === alert.source.id) {
							if (typeof alertType === 'undefined') {
								return true;
							} else if (alertType === alert.alert_type.id) {
								return true;
							}
						}
					}
				})
				.first();
		},

		playAlertNotification() {
			if (!alertsMuted) {
				try {
					const player = new Audio(`/sounds/${alertNotificationSound || 'glock'}.mp3`);
					player.play().catch((error) => {
						console.log('Init Alert Audio Player', { error });
					});
				} catch (error) {
					console.log('Init Alert Audio Player', { error });
				}
			}
		},

		dismissAlert(alertId, reason) {
			const alert = new Alert();
			return alert
				.makePersistRequest({
					url: `${alert.resourceUrl()}${alertId}/dismiss`,
					method: 'POST',
					data: { reason }
				})
				.then(() => {
					this.removeAlert(alertId);
					notify({
						type: 'success',
						title: 'Alert successfully dismissed',
						message: 'The alert was successfully dismissed',
						timeout: 5
					});
				});
		},

		showAlertNotificationAndLogId(alert) {
			if (parseInt(alert.id) > parseInt(lastAlertShownId)) {
				// show the alert
				if (!alert.dismissed) {
					// only show it if it hasn't be dismissed already
					notify({
						type: 'info',
						title: alert.title,
						message: alert.text,
						timeout: 30, // keep for 30 secs so theres enough time to read it!
						url: alert.link ? alert.link : undefined
					});
				}
				// log the id of the last alert shown!
				lastAlertShown.set(alert.id);
			}
		}
	};

	return {
		subscribe,
		update,
		set,
		...methods
	};
};

export default init();
